<template>
  <Loader
    :value="promises.fetchOne"
    :message="$t('loading', { resource: $tc('addonShop').toLowerCase() }) + '...'"
    class="shop-mutate"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #right>
          <div class="row end g-1">
            <ButtonComponent
              v-if="$path('categories.length', item)"
              :label="$t('preview')"
              :icon="['fal', 'eye']"
              tag="a"
              :href="item.url"
              target="_blank"
            />
            <Actions placement="shop" :query="{ shop: id }">
              <template #bottom>
                <ButtonComponent
                  :label="`${$t('shopSync')} ${$tc('shop', 1).toLowerCase()}`"
                  @click="syncDialog = true"
                  :icon="['fal', 'sync-alt']"
                />
                <ButtonComponent
                  v-if="!hasSignatures"
                  :label="`${$t('delete')} ${$tc('shop', 1).toLowerCase()}`"
                  @click="removeDialog = true"
                  :icon="['fal', 'times']"
                />
              </template>
            </Actions>
            <ButtonComponent
              :label="$t('save')"
              :success-label="$t('saved')"
              :loading-label="$t('saving')"
              :icon="['fal', 'arrow-to-bottom']"
              theme="primary"
              :class="{ 'elder-button--disabled': !validator.passes || !hasChanges }"
              v-tooltip="{ content: hasChanges ? null : $t('noUnsavedChanges') }"
              :promise="promises.mutate"
              @click="submit"
            />
          </div>
        </template>
      </HeaderComponent>
      <div class="shop-mutate__actions">
        <DropdownComponent @visible="() => track('Sets')">
          <ButtonComponent :icon="['fal', 'boxes']">
            <template #label>
              <span>{{ $tc('shopSet', 2) }}</span>
              <Badge :value="data.sets.length" class="ml-1" />
            </template>
          </ButtonComponent>
          <template #dropdown>
            <SetButton v-for="set in data.sets" :key="set.id" :value="set" @click="selectedSet = set" />
            <hr v-if="data.sets.length" class="m-0" />
            <ButtonComponent :label="$t('create')" @click="selectedSet = true" :icon="['fal', 'plus']" />
          </template>
        </DropdownComponent>
        <ContextualImages v-model="data.contextualImages" @track="track" />
        <Intro
          :description.sync="data.description"
          :cover.sync="data.cover"
          :label="$t('shopWelcomeText')"
          @track="() => track('ShopWelcome')"
        />
        <PricingMethod v-if="!hasSignatures" v-model="data.pricingMethod" @track="track" />
      </div>
      <Card v-if="item.id" tag="form" class="shop-mutate__content" @submit.prevent="submit">
        <template #default>
          <Categories v-model="data.categories" :selected.sync="selected" @track="track" />
          <Subcategories
            v-if="selected"
            v-model="selected.categories"
            :category="selected"
            :category-index="selectedIndex"
            :pricing-method="data.pricingMethod"
            :categories="data.categories"
            @move="moveProduct"
            @track="track"
          />
        </template>
      </Card>
      <SetsDialog
        v-if="selectedSet"
        :value="selectedSet"
        :shop="data"
        @input="onSetInput"
        @remove="onSetRemove"
        @products:add="onSetAddProducts"
        @products:remove="onSetRemoveProducts"
        @close="selectedSet = null"
      />
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$path('reference.name', item)"
        :action="remove"
        @onSuccess="onSuccessRemove"
        :resource="$tc('addonShop')"
      />
      <SyncComponent v-if="syncDialog" :value="item" @close="syncDialog = false" />
    </div>
  </Loader>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { mapi18n } from '@/i18n'
import Validator from '@/mixins/validator-v2'
import ModelMixin from '@/mixins/Model'
import Storage from '@/mixins/Storage'
import { mergeDeepRight } from 'ramda'
import Model from '@/models/Shop/base'
import ItemModel from '@/models/Shop/item'

import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import Categories from '@/components/Shop/Categories'
import Subcategories from '@/components/Shop/Subcategories'
import Items from '@/components/Shop/Items'
import RemoveComponent from '@/components/Remove'
import SyncComponent from '@/components/Shop/Sync'
import { DropdownComponent } from 'vue-elder-dropdown'
import SetsDialog from '@/components/Shop/Sets/Dialog'
import Badge from '@/components/Badge'
import SetButton from '@/components/Shop/Sets/Button'
import Intro from '@/components/Shop/Intro'
import ContextualImages from '@/components/Shop/ContextualImages'
import { AlertComponent as Alert } from 'vue-elder-alert'
import Actions from '@/components/Actions'
import PricingMethod from '@/components/Shop/PricingMethod'
import tracker from '@/tracking'

const BreadcrumbRouteMap = {
  Residential: { name: 'residential-edit' },
  ResidentialTemplate: { name: 'residential-template-edit' },
}

export default {
  mixins: [
    Validator({
      rules: {
        'categories.*.label': 'required',
        'categories.*.categories.*.label': 'required',
      },
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      model: Model,
      guard: true,
      transform: {
        'categories.*.status': () => undefined,
        'categories.*.items.*.product': v => v.id,
        'categories.*.categories.*.items.*.product': v => v.id,
      },
    }),
    Storage({
      target: 'selectedIndex',
      key: 'shop-category-index',
    }),
  ],
  provide() {
    return {
      validator: this.validator,
      getCategories: () => this.data.categories,
      getItems: () => this.data.categories.flatMap(c => c.categories).flatMap(c => c.items),
      shopTracker: this.track,
    }
  },
  props: {
    id: String,
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      removeDialog: false,
      syncDialog: false,
      selectedSet: false,
      selectedIndex: null,
    }
  },
  computed: {
    ...mapState('Shop', ['promises', 'item']),
    ...mapi18n('labels', {
      loading() {
        return this.$tc('loading') + '...'
      },
      shop: ['addonShop', 1],
    }),
    selected: {
      get() {
        return this.data.categories[this.selectedIndex]
      },
      set(val) {
        this.selectedIndex = this.data.categories.indexOf(val)
      },
    },
    breadcrumb() {
      if (!this.item.id) return []

      switch (this.item.reference.__typename) {
        case 'Residential':
          return [
            { title: this.$tc('project', 2), action: { name: 'projects' } },
            {
              title: this.$path('item.project.name'),
              action: {
                name: 'project-dashboard',
                params: { id: this.$path('item.project.id') },
              },
            },
            {
              title: this.$path('item.reference.name'),
              action: mergeDeepRight(BreadcrumbRouteMap[this.item.reference.__typename], {
                params: { id: this.item.reference.id },
              }),
            },
            { title: this.labels.shop, action: { name: 'shop', params: { id: this.id } } },
            { title: this.$t('edit') },
          ]
        case 'ResidentialTemplate':
          return [
            { title: this.$tc('unitCatalog', 1), action: { name: 'residential-templates' } },
            {
              title: this.$path('item.reference.name'),
              action: { name: 'residential-template-edit', params: { id: this.$path('item.reference.id') } },
            },
            { title: this.labels.shop, action: { name: 'shop', params: { id: this.id } } },
            { title: this.$t('edit') },
          ]
      }
    },
    hasSignatures() {
      if (!this.item.id) return false
      return Boolean(this.item.categories.filter(cat => ['signed', 'accepted'].includes(cat.status)).length)
    },
  },
  methods: {
    ...mapActions('Shop', {
      remove(dispatch, payload) {
        return dispatch('remove', { id: this.id }).then(() => this.resetData())
      },
      fetch(dispatch) {
        return dispatch('fetch', { id: this.id }).then(() => {
          if (!this.item.id) return this.$router.replace({ name: 'notfound' })
        })
      },
      submit(dispatch) {
        if (!this.validator.passes || !this.hasChanges) return

        return dispatch('mutate', [
          { id: this.id, ...this.getData({ diff: true }) },
          {
            strategy: (a, b, raw) => raw,
          },
        ])
      },
    }),
    track(value) {
      tracker.emit(`${value}:shop`)
    },
    moveProduct(data = {}) {
      const { product = {}, isDefault = false, isSelected = false, from = {}, to = {} } = data

      const isSameSubcategoryChange = from.subcategory === to.subcategory
      const props = { selected: isSelected, isDefault: isDefault }

      // TODO: Add tests and add as domain function
      const handlers = {
        resetProperties: (items = [], properties = {}) => {
          const props = Object.entries(properties)
            .map(([key, value]) => (value ? key : null))
            .filter(Boolean)
          if (!props.length || !items.length) return
          items.forEach(item => {
            if (item.id === product.id) return
            props.forEach(property => {
              item[property] = false
            })
          })
        },
        updateProperties(items = [], properties = {}) {
          if (!items.length) return
          items.forEach(item => {
            if (item.id !== product.id) return
            Object.keys(properties).forEach(property => {
              item[property] = properties[property]
            })
          })
        },
        updateSelected: (items = []) => {
          // Sets the single product to selected
          if (items.length === 1 && !props.isDefault && props.selected) {
            items[0].selected = true
            return
          }

          // Sets the default single product to selected regardless of selected
          if (items.length === 1 && props.isDefault) {
            items[0].selected = true
            return
          }

          // Sets the default product to selected if no other selected
          if (props.isDefault && items.every(item => !item.selected) && !props.selected) {
            items[0].selected = true
            return
          }
        },
        clearSettings: () => {
          if (isSameSubcategoryChange && !isDefault) return
          product.conditions = ItemModel().conditions
        },
        removeProduct: (items = []) => {
          if (!items.length) return
          const index = items.findIndex(item => item.id === product.id)
          if (index < 0) return
          items.splice(index, 1)
        },
        addProduct: (items = []) => {
          if (items.includes(product)) return
          if (isDefault) {
            items.unshift(product)
            return
          }
          items.push(product)
        },
        handleRemove: (items = []) => {
          handlers.removeProduct(items)
          // Sets the first default product to selected if no other selected
          // If no default "no standard" is selected
          const hasNoneSelectedAfterRemove = items.length && items.every(item => !item.selected) && items[0].isDefault
          if (hasNoneSelectedAfterRemove) {
            items[0].selected = true
          }
        },
        handleMove: (items = [], props) => {
          handlers.addProduct(items)
          handlers.resetProperties(items, props)
          handlers.updateProperties(items, props)
          handlers.updateSelected(items)
          handlers.clearSettings()
        },
      }

      const executeOnSubcategoryItems = (direction = {}, handler) => {
        const category = this.data.categories.find(c => c.id === direction.category)
        if (!category) return
        category.categories.forEach(subcategory => {
          if (subcategory.id !== direction.subcategory) return
          handler(subcategory.items)
        })
      }

      if (isSameSubcategoryChange) {
        executeOnSubcategoryItems(from, items => handlers.handleMove(items, props))
        return
      }
      executeOnSubcategoryItems(from, handlers.handleRemove)
      executeOnSubcategoryItems(to, items => handlers.handleMove(items, props))
    },
    onSetInput(value) {
      this.selectedSet = null
      let index = this.data.sets.findIndex(s => s.id === value.id)
      if (index < 0) return this.data.sets.push(value)
      return this.data.sets.splice(index, 1, value)
    },
    onSetRemove(value) {
      this.data.sets = this.data.sets.filter(s => s !== value)
    },
    onSetAddProducts({ ids, set }) {
      this.data.categories.forEach(category => {
        category.categories.forEach(subcategory => {
          subcategory.items.forEach(item => {
            if (!ids.includes(item.id)) return
            if (!item.sets) item.sets = []
            item.sets.push(set.id)
          })
        })
      })
    },
    onSetRemoveProducts({ ids, set }) {
      this.data.categories.forEach(category => {
        category.categories.forEach(subcategory => {
          subcategory.items.forEach(item => {
            if (!ids.includes(item.id)) return
            if (!item.sets) item.sets = []
            item.sets = item.sets.filter(s => s !== set.id)
          })
        })
      })
    },
    onSuccessRemove() {
      switch (this.item.reference.__typename) {
        case 'Residential':
          return this.$router.push({
            name: 'project-dashboard',
            params: { id: this.$path('item.project.id') },
          })
        case 'ResidentialTemplate':
          return this.$router.push({
            name: 'residential-template-edit',
            params: { id: this.$path('item.reference.id') },
          })
      }
    },
  },
  metaInfo() {
    return {
      title: this.item.id ? `${this.$path('item.reference.name')} - ${this.labels.shop}` : this.labels.loading,
    }
  },
  components: {
    Card,
    HeaderComponent,
    Categories,
    Subcategories,
    Items,
    DropdownComponent,
    RemoveComponent,
    SyncComponent,
    SetsDialog,
    Badge,
    SetButton,
    Intro,
    Alert,
    Actions,
    PricingMethod,
    ContextualImages,
  },
}
</script>

<style lang="scss">
.shop-mutate {
  background-color: $background-grey;

  .header {
    margin-bottom: 1rem;
  }

  &__content {
    background-color: transparent;

    & > .card__content {
      padding: 0;
    }
  }

  &__actions {
    display: flex;
    gap: 1rem;
    font-size: 0.8em;
    justify-content: end;
    padding: 1rem 0;
    border-radius: var(--border-radius);
    border-top: 1px solid var(--border-color);
  }
}
</style>
